import { SubTabsNav } from '@/components/ui/sub-tabs-nav';

/**
 * ContractSubTabsNav (C20-133) — horizontal sub-tab strip rendered at the
 * top of every Contract Compliance sub-route. The prototype's
 * `screens-modules-tabs.js → subTabs()` helper renders this strip as the
 * second-level navigation under each module heading; we port it 1:1 to
 * TanStack Router links so each tab is a real navigable URL.
 *
 * Tabs (verbatim from the prototype's `CONTRACT_TABS` constant):
 *   - Baseline        → /engagements/:id/contract/baseline   (also the bare /contract entry)
 *   - Deliverables    → /engagements/:id/contract/deliverables
 *   - Change orders   → /engagements/:id/contract/changes
 *   - IV&V checkpoints→ /engagements/:id/contract/checkpoints
 *   - Vendor scoring  → /engagements/:id/contract/vendor
 *   - Deviations      → /engagements/:id/contract/deviations
 *   - Pulse reports   → /engagements/:id/contract/pulse
 *
 * Visual contract: prototype `.tabs > .tab` rule — horizontal flex with
 * 18px gap, bottom 1px border on hover, accent-amber underline on active.
 * The strip itself is the shared `SubTabsNav` (components/ui, T-0242), so
 * Health & Readiness draws the same one.
 */

const TABS = [
  { slug: 'baseline', label: 'Baseline' },
  { slug: 'deliverables', label: 'Deliverables' },
  { slug: 'changes', label: 'Change orders' },
  { slug: 'checkpoints', label: 'IV&V checkpoints' },
  { slug: 'vendor', label: 'Vendor scoring' },
  { slug: 'deviations', label: 'Deviations' },
  { slug: 'pulse', label: 'Pulse reports' },
  { slug: 'integrations', label: 'Integrations' },
] as const;

export type ContractSubTabSlug = (typeof TABS)[number]['slug'];

export interface ContractSubTabsNavProps {
  engagementId: string;
  active: ContractSubTabSlug;
}

export function ContractSubTabsNav({ engagementId, active }: ContractSubTabsNavProps) {
  return (
    <SubTabsNav
      label="Contract Compliance sub-navigation"
      active={active}
      tabs={TABS.map((t) => ({
        key: t.slug,
        label: t.label,
        to: `/engagements/$engagementId/contract/${t.slug}` as const,
        params: { engagementId },
      }))}
    />
  );
}

export const CONTRACT_SUB_TABS = TABS;
